import { DateTime } from "luxon";

import { Version } from "@/lib/drivers/insulet/OmnipodTypes";

export class IBFRecord {
  buffer: DataView<ArrayBuffer>;
  private _offset: number;

  constructor(buffer: DataView<ArrayBuffer>) {
    this.buffer = buffer;
    this._offset = 0;
  }

  get offset(): number {
    return this._offset;
  }

  restart() {
    this._offset = 0;
  }

  /* the number of bytes left in the IBFRecord that have not been consumed. */
  remaining(): number {
    return this.buffer.byteLength - this._offset;
  }

  /* skips the specified number of bytes. */
  skip(n: number) {
    this._offset += n;
  }

  nextUInt8(): number {
    const value = this.buffer.getUint8(this._offset);
    this._offset += 1;
    return value;
  }

  /* the next byte (int8) */
  nextInt8(): number {
    const value = this.buffer.getInt8(this._offset);
    this._offset += 1;
    return value;
  }

  /* the next unsigned short (uint16) in little-endian format */
  nextUInt16LE(): number {
    const value = this.buffer.getUint16(this._offset, true);
    this._offset += 2;
    return value;
  }

  /* the next unsigned short (uint16) in big-endian format */
  nextUInt16BE(): number {
    const value = this.buffer.getUint16(this._offset);
    this._offset += 2;
    return value;
  }

  /* the next signed short (int16) in little-endian format */
  nextInt16LE(): number {
    const value = this.buffer.getInt16(this._offset, true);
    this._offset += 2;
    return value;
  }

  /* the next signed short (int16) in big-endian format */
  nextInt16BE(): number {
    const value = this.buffer.getInt16(this._offset);
    this._offset += 2;
    return value;
  }

  /* the next unsigned integer (uint32) in little-endian format */
  nextUInt32LE(): number {
    const value = this.buffer.getUint32(this._offset, true);
    this._offset += 4;
    return value;
  }

  /* the next unsigned integer (uint32) in big-endian format */
  nextUInt32BE(): number {
    const value = this.buffer.getUint32(this._offset);
    this._offset += 4;
    return value;
  }

  /* the next signed integer (int32) in little-endian format */
  nextInt32LE(): number {
    const value = this.buffer.getInt32(this._offset, true);
    this._offset += 4;
    return value;
  }

  /* the next signed integer (int32) in big-endian format */
  nextInt32BE(): number {
    const value = this.buffer.getInt32(this._offset);
    this._offset += 4;
    return value;
  }

  /* the next date (Y-m-d H:m:s), inferred to be in the local time zone */
  nextDateTime(): DateTime {
    const day = this.nextInt8();
    const month = this.nextInt8();
    const year = this.nextUInt16LE();
    const seconds = this.nextInt8();
    const minutes = this.nextInt8();
    const hours = this.nextInt8();

    return DateTime.fromObject({
      year: year,
      month: month,
      day: day,
      hour: hours,
      minute: minutes,
      second: seconds,
    });
  }

  /* the next version, as major.minor.patch with numerical components */
  nextVersion(): Version {
    const major = this.nextUInt16BE();
    const minor = this.nextUInt16BE();
    const patch = this.nextUInt16BE();

    return { major, minor, patch };
  }

  /*
   * nextString reads a null-terminated string from a fixed-width field.
   *
   * Any bytes after the first null byte in the fixed-width field will be ignored.
   */
  nextString(fixedLength?: number): string {
    let idx = this._offset;
    while (this.buffer.getInt8(idx) != 0x00) {
      idx++;
    }
    const strBuffer = this.buffer.buffer.slice(this._offset, idx) as ArrayBuffer;
    this._offset += fixedLength ?? strBuffer.byteLength + 1;
    const textEncoder = new TextDecoder("windows-1252");
    return textEncoder.decode(strBuffer);
  }

  subRecord(size?: number): IBFRecord {
    size = size ?? this.remaining();
    const subBuffer = this.buffer.buffer.slice(this._offset, this._offset + size);
    this._offset += size;
    return new IBFRecord(new DataView(subBuffer));
  }
}
